import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNotification } from '../../../../contexts/NotificationContext';
import { DRAFT_KEYS, loadDraft, hasDraft, clearDraft, saveDraft } from '../../../../utils/draftUtils';
import { 
  fetchPatientData, 
  fetchAnalgesiaAssessment, 
  fetchNextIpNo,
  submitAnalgesiaAssessment, 
  updateAnalgesiaAssessment,
  generateShareToken
} from '../api/analgesiaApi';
import { mapBackendToForm, mapFormToBackend } from '../utils/mapping';
import { useAnalgesiaValidation } from './useAnalgesiaValidation';

export const useAnalgesiaAssessment = (uhid_no, navigate) => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();
  
  const [existingId, setExistingId] = useState(null);
  const [submitStatus, setSubmitStatus] = useState({ open: false, message: "", severity: "success" });

  const [patientData, setPatientData] = useState({
    ip_no: "",
    uhid_no: uhid_no || "",
    patient_name: "",
    age: "",
    sex: "",
    ward_no: "",
    room_no: "",
    assessment_date: "",
    anesthesiologist: "",
    surgeon: "",
    surgical_procedure: "",
    perioperative_analgesia_plan: "",
    preoperative: "",
    intraoperative: "",
    postoperative: ""
  });

  const [rows, setRows] = useState([
    {
      id: 1,
      assessment_time: "",
      pulse_rate: "",
      blood_pressure: "",
      respiratory_rate: "",
      spo2: "",
      vas_score: "",
      sedation: "",
      rescue_analgesia_given: "",
      staff_nurse: ""
    }
  ]);

  const { errors, setErrors, validateField, validateForm } = useAnalgesiaValidation(patientData);

  // Queries
  const { data: patientInfo, isLoading: isPatientInfoLoading } = useQuery({
    queryKey: ['patient', uhid_no],
    queryFn: () => fetchPatientData(uhid_no),
    enabled: !!uhid_no
  });

  const { data: nextIpData } = useQuery({
    queryKey: ['analgesiaNextIp'],
    queryFn: fetchNextIpNo,
    enabled: !existingId
  });

  const { data: existingData, isLoading: isExistingDataLoading } = useQuery({
    queryKey: ['analgesiaAssessment', uhid_no],
    queryFn: () => fetchAnalgesiaAssessment(uhid_no),
    enabled: !!uhid_no,
    retry: false
  });

  useEffect(() => {
    if (existingData) {
      const mapped = mapBackendToForm(existingData, uhid_no);
      if (mapped) {
        setExistingId(mapped.existingId);
        setPatientData(prev => ({ ...prev, ...mapped.patientData }));
        // Only override rows if no draft exists
        if (!hasDraft(DRAFT_KEYS.POST_OPERATIVE_ANALGESIA_ASSESSMENT, uhid_no)) {
          setRows(mapped.rows);
        }
      }
    }
  }, [existingData, uhid_no]);

  // Effects
  useEffect(() => {
    if (patientInfo) {
      setPatientData(prev => ({ 
        ...prev, 
        patient_name: patientInfo.name,
        age: patientInfo.age || '',
        sex: patientInfo.sex || '',
      }));
    }
  }, [patientInfo]);

  useEffect(() => {
    if (nextIpData && !existingId) {
      setPatientData(prev => ({ ...prev, ip_no: nextIpData.next_ip_no }));
    }
  }, [nextIpData, existingId]);

  // Draft loading and saving
  useEffect(() => {
    if (uhid_no && hasDraft(DRAFT_KEYS.POST_OPERATIVE_ANALGESIA_ASSESSMENT, uhid_no)) {
      const saved = loadDraft(DRAFT_KEYS.POST_OPERATIVE_ANALGESIA_ASSESSMENT, uhid_no);
      if (saved && saved.formData) {
        const { assessment_rows, ...pd } = saved.formData;
        setPatientData(prev => ({ ...prev, ...pd }));
        if (Array.isArray(assessment_rows)) {
          setRows(assessment_rows);
        }
      }
    }
  }, [uhid_no]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (uhid_no) {
        saveDraft(DRAFT_KEYS.POST_OPERATIVE_ANALGESIA_ASSESSMENT, uhid_no, {
          ...patientData,
          assessment_rows: rows
        });
      }
    }, 2000);
    return () => clearTimeout(timeoutId);
  }, [patientData, rows, uhid_no]);

  // Handlers
  const handlePatientChange = useCallback((event) => {
    const { name, value } = event.target;
    setPatientData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  }, [errors, setErrors]);

  const updateRow = useCallback((rowId, field, value) => {
    setRows(prev => prev.map(row => 
      row.id === rowId ? { ...row, [field]: value } : row
    ));
  }, []);

  const addRow = useCallback(() => {
    setRows(prev => [
      ...prev,
      {
        id: prev.length > 0 ? Math.max(...prev.map(r => r.id)) + 1 : 1,
        assessment_time: "",
        pulse_rate: "",
        blood_pressure: "",
        respiratory_rate: "",
        spo2: "",
        vas_score: "",
        sedation: "",
        rescue_analgesia_given: "",
        staff_nurse: ""
      }
    ]);
  }, []);

  const deleteRow = useCallback((id) => {
    if (rows.length > 1) {
      setRows(prev => prev.filter(row => row.id !== id));
    } else {
      showNotification('warning', 'At least one row must remain.');
    }
  }, [rows.length, showNotification]);

  const handleBlur = useCallback((event) => {
    const { name, value } = event.target;
    validateField(name, value);
  }, [validateField]);

  // Mutation
  const mutation = useMutation({
    mutationFn: (data) => {
      if (existingId) {
        return updateAnalgesiaAssessment(existingId, data);
      } else {
        return submitAnalgesiaAssessment(data);
      }
    },
    onSuccess: () => {
      showNotification('success', existingId ? 'Assessment updated successfully!' : 'Assessment submitted successfully!');
      queryClient.invalidateQueries(['analgesiaAssessment', uhid_no]);
      clearDraft(DRAFT_KEYS.POST_OPERATIVE_ANALGESIA_ASSESSMENT, uhid_no);
      setTimeout(() => {
        navigate(`/mainPage/${uhid_no}`, {
          state: { from: 'PostOperativePhase', status: 'success', step: 3 },
        });
      }, 1000);
    },
    onError: (error) => {
      console.error('Submission error:', error);
      showNotification('error', error.response?.data?.error || 'Error in submitting form!');
    }
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!validateForm()) return;
    mutation.mutate(mapFormToBackend(patientData, rows));
  };

  const handleShare = async () => {
    try {
      const res = await generateShareToken(uhid_no);
      const link = `${window.location.origin}/postOperativeAssesment/${uhid_no}?token=${res.token}`;
      const message = `Dear Doctor,\n\nPlease fill the post-operative analgesia chart:\n\n${link}`;
      await navigator.clipboard.writeText(link);
      setSubmitStatus({ open: true, message: "Secure link copied to clipboard!", severity: "info" });
      const phoneNumber = '+916379605575';
      window.open(`http://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
    } catch (error) {
      console.error("Error generating share token:", error);
      setSubmitStatus({ open: true, message: "Failed to generate shareable link", severity: "error" });
    }
  };

  return {
    patientData, setPatientData,
    rows, setRows,
    errors, setErrors,
    existingId,
    loading: isPatientInfoLoading || isExistingDataLoading,
    submitStatus, setSubmitStatus,
    handlePatientChange, handleBlur, updateRow, addRow, deleteRow,
    handleSubmit, handleShare,
    mutationLoading: mutation.isLoading
  };
};
